import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import { ArrowUpRight, ShieldCheck, Sparkles } from "lucide-react";
import gsap from "gsap";

const LOGIN_HERO =
  "https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=2400&q=80";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { t } = useTranslation();
  const root = useRef(null);

  const onGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".login-eyebrow", {
        opacity: 0,
        y: 20,
        duration: 0.9,
        ease: "power3.out",
        delay: 0.1,
      });
      gsap.from(".login-title", {
        opacity: 0,
        y: 50,
        duration: 1.4,
        ease: "power4.out",
        delay: 0.2,
      });
      gsap.from(".login-intro", {
        opacity: 0,
        y: 24,
        duration: 1.1,
        ease: "power3.out",
        delay: 0.5,
      });
      gsap.from(".login-card", {
        opacity: 0,
        y: 60,
        duration: 1.3,
        ease: "power4.out",
        delay: 0.55,
      });
      gsap.fromTo(
        ".login-bg",
        { scale: 1.18 },
        { scale: 1, duration: 2.4, ease: "power3.out" }
      );
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <SmoothScroll>
      <div ref={root} className="min-h-screen bg-slate-950 text-white overflow-hidden">
        <SEO
          title="Sign in — Across Indonesia"
          description="One-tap sign-in with Google. Pick up where you left off — your itineraries, bookings and documents."
          image={LOGIN_HERO}
        />
        <Navbar transparent />

        {/* Full-bleed cinematic stage */}
        <section className="relative min-h-screen w-full flex items-center" data-testid="login-page">
          <img
            src={LOGIN_HERO}
            alt="A quiet Indonesian sunrise"
            className="login-bg absolute inset-0 w-full h-full object-cover"
          />
          {/* Layered overlays for cinematic depth */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(141,170,160,0.18),transparent_60%)]" />

          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 py-32 grid md:grid-cols-12 gap-16 items-center">
            {/* Left — narrative */}
            <div className="md:col-span-7">
              <div className="login-eyebrow inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[10px] font-semibold uppercase tracking-[0.3em] text-seafoam">
                <Sparkles size={11} strokeWidth={1.8} />
                {t("login.eyebrow")}
              </div>

              <h1
                className="login-title mt-8 font-light tracking-tight leading-[0.95]"
                style={{ fontSize: "clamp(3rem, 7.5vw, 6.5rem)" }}
              >
                {t("login.title_a")}{" "}
                <b className="font-bold">{t("login.title_b")}</b>
              </h1>

              <p className="login-intro mt-8 text-lg md:text-xl text-white/75 max-w-xl leading-relaxed font-light">
                {t("login.intro")}
              </p>

              <div className="login-intro mt-10 hidden md:flex items-center gap-8 text-sm text-white/55">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} strokeWidth={1.5} className="text-seafoam" />
                  <span>Encrypted by Google</span>
                </div>
                <div className="w-px h-5 bg-white/15" />
                <div>No passwords · No spam</div>
              </div>
            </div>

            {/* Right — glass sign-in card */}
            <div className="md:col-span-5 md:pl-6">
              <div
                className="login-card relative rounded-3xl p-10 md:p-12 bg-white/[0.06] border border-white/15 backdrop-blur-xl shadow-2xl overflow-hidden"
              >
                <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-seafoam/20 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-sky/10 blur-3xl pointer-events-none" />

                <div className="relative">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-white/55 mb-3">
                    Continue with
                  </div>
                  <h2 className="text-3xl font-light tracking-tight leading-tight">
                    Your quiet <b className="font-bold">trip planner.</b>
                  </h2>
                  <p className="mt-4 text-sm text-white/60 leading-relaxed">
                    Sign in once — your bookings, consultations and travel
                    documents stay with you across every device.
                  </p>

                  <button
                    data-testid="google-login-btn"
                    onClick={onGoogle}
                    className="mt-9 group w-full flex items-center justify-center gap-3 py-4 px-6 rounded-full bg-white text-slate-900 font-medium text-sm tracking-wide hover:bg-stone-100 transition-all duration-300 shadow-2xl hover:shadow-seafoam/20 hover:scale-[1.02]"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {t("login.continue_google")}
                    <ArrowUpRight
                      size={16}
                      strokeWidth={1.8}
                      className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </button>

                  <p className="mt-6 text-[11px] text-white/45 leading-relaxed">
                    {t("login.terms")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subtle scroll cue */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase text-white/40">
            Across Indonesia · est. 2023
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
